import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getRandomContent, recordProgress, awardBonus, Content } from '../services/api';
import { speak } from '../services/audio';
import './Games.css';

const AnagramGame: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lessonId = searchParams.get('lessonId') || undefined;
  const customSetId = searchParams.get('customSetId') || undefined;

  const [items, setItems] = useState<Content[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [scrambledLetters, setScrambledLetters] = useState<string[]>([]);
  const [selectedLetters, setSelectedLetters] = useState<string[]>([]);
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);

  const scrambleWord = useCallback((word: string): string[] => {
    // Remove spaces and punctuation for scrambling
    const cleanWord = word.replace(/[^a-zA-Z]/g, '').toLowerCase();
    const letters = cleanWord.split('');
    // Shuffle letters
    for (let i = letters.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [letters[i], letters[j]] = [letters[j], letters[i]];
    }
    // Ensure it's actually scrambled
    if (letters.join('') === cleanWord && letters.length > 1) {
      letters.reverse();
    }
    return letters;
  }, []);

  useEffect(() => {
    const loadItems = async () => {
      try {
        const data = await getRandomContent(20, lessonId, customSetId);
        // Filter for single words only (no spaces), and reasonable length
        const words = data.filter(item => {
          const clean = item.english.replace(/[^a-zA-Z\s]/g, '');
          return !clean.includes(' ') && clean.length >= 3 && clean.length <= 12;
        });
        setItems(words.slice(0, 10));
      } catch (err) {
        console.error('Failed to load content:', err);
      } finally {
        setLoading(false);
      }
    };
    loadItems();
  }, [lessonId, customSetId]);

  useEffect(() => {
    if (items.length > 0 && currentIndex < items.length) {
      setScrambledLetters(scrambleWord(items[currentIndex].english));
      setSelectedLetters([]);
    }
  }, [items, currentIndex, scrambleWord]);

  const handleLetterClick = (letter: string, index: number) => {
    if (feedback) return;
    setSelectedLetters([...selectedLetters, letter]);
    setScrambledLetters(scrambledLetters.filter((_, i) => i !== index));
  };

  const handleSelectedLetterClick = (letter: string, index: number) => {
    if (feedback) return;
    setScrambledLetters([...scrambledLetters, letter]);
    setSelectedLetters(selectedLetters.filter((_, i) => i !== index));
  };

  const handleCheck = async () => {
    const currentItem = items[currentIndex];
    const userAnswer = selectedLetters.join('').toLowerCase();
    const correctAnswer = currentItem.english.replace(/[^a-zA-Z]/g, '').toLowerCase();
    const isCorrect = userAnswer === correctAnswer;

    setFeedback(isCorrect ? 'correct' : 'wrong');
    await recordProgress(currentItem.id, isCorrect);
    
    speak(currentItem.english);

    setScore(prev => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      total: prev.total + 1
    }));
  };

  const handleNext = () => {
    if (currentIndex < items.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setFeedback(null);
    } else {
      handleComplete();
    }
  };

  const handleComplete = async () => {
    if (score.correct + (feedback === 'correct' ? 1 : 0) === items.length) {
      await awardBonus(30, 'perfect_anagram');
    }
    setCompleted(true);
  };

  if (loading) return <div className="loading">Ładowanie...</div>;
  if (items.length < 4) {
    return (
      <div className="game-error">
        <h2>Za mało słówek!</h2>
        <p>Potrzebujesz minimum 4 słówek do tej gry.</p>
        <button className="btn btn-primary" onClick={() => navigate('/games')}>Wróć</button>
      </div>
    );
  }

  const currentItem = items[currentIndex];
  if (!currentItem) return <div className="loading">Ładowanie...</div>;

  if (completed) {
    const isPerfect = score.correct === items.length;
    return (
      <div className="game-complete fade-in">
        <div className="complete-icon">{isPerfect ? '🏆' : '🎉'}</div>
        <h1>{isPerfect ? 'Perfekcyjnie!' : 'Świetnie!'}</h1>
        <p>Wynik: {score.correct} / {score.total}</p>
        {isPerfect && <div className="bonus-badge bounce">+30 bonus!</div>}
        <button className="btn btn-primary btn-large" onClick={() => window.location.reload()}>
          Zagraj ponownie
        </button>
      </div>
    );
  }

  return (
    <div className="game-container fade-in">
      <div className="game-header">
        <h2>🔤 Anagram</h2>
        <div className="game-progress">{currentIndex + 1} / {items.length}</div>
      </div>

      <div className="game-score">✅ {score.correct} / {score.total}</div>

      <div className="anagram-card">
        <div className="polish-word">{currentItem.polish}</div>
        <p className="quiz-instruction">Ułóż litery w poprawnej kolejności:</p>

        <div className={`anagram-answer-area ${feedback || ''}`}>
          {selectedLetters.length === 0 ? (
            <span className="placeholder-text">Kliknij litery poniżej...</span>
          ) : (
            selectedLetters.map((letter, index) => (
              <button
                key={index}
                className="letter-chip selected"
                onClick={() => handleSelectedLetterClick(letter, index)}
                disabled={!!feedback}
              >
                {letter.toUpperCase()}
              </button>
            ))
          )}
        </div>

        <div className="letter-bank">
          {scrambledLetters.map((letter, index) => (
            <button
              key={index}
              className="letter-chip"
              onClick={() => handleLetterClick(letter, index)}
              disabled={!!feedback}
            >
              {letter.toUpperCase()}
            </button>
          ))}
        </div>

        {feedback === 'wrong' && (
          <div className="correct-answer">
            Poprawna odpowiedź: <strong>{currentItem.english}</strong>
            <button className="speak-btn-inline" onClick={() => speak(currentItem.english)}>🔊</button>
          </div>
        )}

        {!feedback && scrambledLetters.length === 0 && (
          <button className="btn btn-primary" onClick={handleCheck}>
            Sprawdź ➜
          </button>
        )}

        {feedback && (
          <button className="btn btn-primary btn-next" onClick={handleNext}>
            Dalej ➜
          </button>
        )}
      </div>
    </div>
  );
};

export default AnagramGame;

